/**
 * Opening a PDF of the bench in the browser: a parcel's carrier label (the PDF
 * the server saved when the carrier created the shipment), or our own box
 * barcodes of an order opened by hand.
 *
 * Not an `<a href>`: the route is authenticated and a plain link sends no
 * header. The bytes are fetched with the token and handed to a new tab as a
 * blob, where the browser's own print takes over — the bench does not drive
 * a printer.
 *
 * The tab is opened **before** the fetch, while the tap is still a user
 * gesture: a `window.open` after an `await` is what popup blockers stop.
 */

import { identitaCorrente } from '@errebi/sessione';

/** Why the label could not be opened, in the server's words when it gave some. */
export class LabelUnavailable extends Error {}

export function openLabel(barcode: string): Promise<void> {
  return openPdf(
    `/api/flusso/fine-linea/etichetta/${encodeURIComponent(barcode)}`,
    `Etichetta di ${barcode} non disponibile.`,
    `Non riesco a leggere l’etichetta di ${barcode}.`,
  );
}

/**
 * Our own box barcodes of a manual order, to stick on before the scale: all
 * the boxes, or only `progressivo` for a reprint.
 */
export function openParcelBarcodes(ordine: string, progressivo?: number): Promise<void> {
  const which = progressivo === undefined ? '' : `?progressivo=${progressivo}`;
  return openPdf(
    `/api/flusso/fine-linea/${encodeURIComponent(ordine)}/barcode-scatole${which}`,
    `Barcode delle scatole di ${ordine} non disponibili.`,
    `Non riesco a leggere i barcode delle scatole di ${ordine}.`,
  );
}

/** Every carrier label of an order in one PDF: «Stampa etichette» in «Oggi». */
export function openCarrierLabels(ordine: string): Promise<void> {
  return openPdf(
    `/api/flusso/fine-linea/${encodeURIComponent(ordine)}/etichette-corriere`,
    `Etichette di ${ordine} non disponibili.`,
    `Non riesco a leggere le etichette di ${ordine}.`,
  );
}

async function openPdf(url: string, unavailable: string, unreadable: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const token = identitaCorrente()?.token;
    const res = await fetch(url, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Nest's HttpException puts the reason in `message`; `motivo` is the
      // shape of our own refusals, kept in case the route ever answers so.
      const body = (await res.json().catch(() => null)) as {
        message?: unknown;
        motivo?: unknown;
      } | null;
      const reason = [body?.message, body?.motivo].find((x) => typeof x === 'string' && x !== '');
      throw new LabelUnavailable(typeof reason === 'string' ? reason : unavailable);
    }
    const blobUrl = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = blobUrl;
    else window.location.assign(blobUrl);
  } catch (e) {
    tab?.close();
    throw e instanceof LabelUnavailable ? e : new LabelUnavailable(unreadable);
  }
}
